import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiSend,
  FiCheck,
  FiAlertCircle,
  FiLoader,
} from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./Contact.css";

/* ── hero background ── */
const heroBg =
  "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&q=80";

/* ── contact info cards ── */
const contactCards = [
  {
    icon: FiMapPin,
    label: "Visit Us",
    lines: [
      "52 Jakaranda Street",
      "Hennopspark, Centurion, 0157",
      "South Africa",
    ],
    action: {
      text: "Get Directions",
      href: "https://maps.google.com/?q=52+Jakaranda+Street,+Hennopspark,+Centurion,+0157",
    },
  },
  {
    icon: FiPhone,
    label: "Call Us",
    lines: ["012 004-1000", "072 623 4209"],
    action: { text: "Call Now", href: "tel:+27120041000" },
  },
  {
    icon: FiMail,
    label: "Email Us",
    lines: ["sales@bakwenakit.co.za", "tshepo.moepya@bakwenakit.co.za"],
    action: { text: "Send Email", href: "mailto:sales@bakwenakit.co.za" },
  },
  {
    icon: FiClock,
    label: "Office Hours",
    lines: ["Mon – Fri: 07:00 – 17:00", "Saturday: 08:00 – 13:00", "Sunday: Closed"],
    action: null,
  },
];

/* ── disable right-click ── */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ════════════════════════════════════════════════════════════ */
export default function Contact() {
  useNoContextMenu();

  const [heroRef,  heroInView]  = useInView();
  const [cardsRef, cardsInView] = useInView();
  const [formRef,  formInView]  = useInView();
  const [mapRef,   mapInView]   = useInView();

  /* ── form state ── */
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", subject: "", message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      /*
       * POST to your backend endpoint (server.js / Express).
       * The backend holds the SMTP password in an environment variable —
       * it is never exposed here in the frontend.
       *
       * If your React app is served from the same Express server,
       * the relative path "/api/send-email" works as-is.
       * If they are on different origins, replace with the full URL,
       * e.g. "https://api.bakwenakit.co.za/send-email"
       */
      const res = await fetch("/api/send-email", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:    form.name,
          email:   form.email,
          phone:   form.phone,
          company: form.company,
          subject: form.subject,
          message: form.message,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || `Server error ${res.status}`);
      }

      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <main className="ct">

      {/* ══════ 1. HERO ══════ */}
      <section className="ct-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="ct-hero__overlay" />
        <div className="container ct-hero__body" ref={heroRef}>
          <div className={`ct-hero__label fade-in ${heroInView ? "visible" : ""}`}>
            CONTACT US
          </div>
          <h1
            className={`ct-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            LET'S BUILD SOUTH<br />AFRICA TOGETHER
          </h1>
        </div>
        <div className="ct-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>Contact Us</span>
        </div>
      </section>

      {/* ══════ 2. CONTACT CARDS ══════ */}
      <section className="ct-cards" ref={cardsRef}>
        <div className="container ct-cards__grid">
          {contactCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div
                key={i}
                className={`ct-card fade-in ${cardsInView ? "visible" : ""}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="ct-card__icon-wrap">
                  <Icon size={24} />
                </div>
                <div className="ct-card__label">{card.label}</div>
                <div className="ct-card__lines">
                  {card.lines.map((l, j) => (
                    <span key={j}>{l}</span>
                  ))}
                </div>
                {card.action && (
                  <a
                    href={card.action.href}
                    className="ct-card__action"
                    target={card.action.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                  >
                    {card.action.text} <FiArrowRight size={13} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════ 3. FORM + SIDEBAR ══════ */}
      <section className="ct-main" ref={formRef}>
        <div className="container ct-main__inner">

          {/* ── Left: contact form ── */}
          <div className={`ct-form-wrap fade-in ${formInView ? "visible" : ""}`}>
            <div className="ct-section-tag">SEND A MESSAGE</div>
            <h2 className="ct-section-h2">
              Get In Touch With<br /><span>Our Team</span>
            </h2>
            <p className="ct-section-sub">
              Fill in the form below and one of our team members will respond within one business day.
            </p>

            <form className="ct-form" onSubmit={handleSubmit} noValidate>
              <div className="ct-form__row">
                <div className="ct-form__field">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    id="name" name="name" type="text" required
                    placeholder="John Smith"
                    value={form.name} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
                <div className="ct-form__field">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    id="email" name="email" type="email" required
                    placeholder="john@company.co.za"
                    value={form.email} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
              </div>

              <div className="ct-form__row">
                <div className="ct-form__field">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone" name="phone" type="tel"
                    placeholder="012 000 0000"
                    value={form.phone} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
                <div className="ct-form__field">
                  <label htmlFor="company">Company / Organisation</label>
                  <input
                    id="company" name="company" type="text"
                    placeholder="Your company name"
                    value={form.company} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
              </div>

              <div className="ct-form__field">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject" name="subject" required
                  value={form.subject} onChange={handleChange}
                  disabled={status === "sending"}
                >
                  <option value="">— Select a subject —</option>
                  <option value="Formwork Enquiry">Formwork Enquiry</option>
                  <option value="Scaffolding Enquiry">Scaffolding Enquiry</option>
                  <option value="Project Quote">Request a Project Quote</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Partnership">Partnership / Subcontracting</option>
                  <option value="General">General Enquiry</option>
                </select>
              </div>

              <div className="ct-form__field">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message" name="message" required
                  rows={6}
                  placeholder="Tell us about your project, site requirements, timeline, or any questions you have..."
                  value={form.message} onChange={handleChange}
                  disabled={status === "sending"}
                />
              </div>

              {/* Status feedback */}
              {status === "success" && (
                <div className="ct-form__feedback ct-form__feedback--success">
                  <FiCheck size={18} />
                  <span>Thank you! Your message has been sent. We'll be in touch shortly.</span>
                </div>
              )}
              {status === "error" && (
                <div className="ct-form__feedback ct-form__feedback--error">
                  <FiAlertCircle size={18} />
                  <span>{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                className="ct-form__submit"
                disabled={status === "sending" || status === "success"}
              >
                {status === "sending" ? (
                  <>
                    <FiLoader size={16} className="ct-spin" /> Sending…
                  </>
                ) : status === "success" ? (
                  <>
                    <FiCheck size={16} /> Message Sent
                  </>
                ) : (
                  <>
                    Send Message <FiSend size={14} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* ── Right: sidebar info ── */}
          <aside className={`ct-aside fade-in ${formInView ? "visible" : ""}`} style={{ transitionDelay: "0.15s" }}>

            <div className="ct-aside__block">
              <div className="ct-section-tag">OUR BRANCH</div>
              <ul className="ct-branches">
                <li>
                  <span className="ct-branches__dot" />
                  52 Jakaranda St, Hennopspark, Centurion, 0157, South Africa
                </li>
              </ul>
            </div>

            <div className="ct-aside__block">
              <div className="ct-section-tag">CERTIFICATIONS</div>
              <ul className="ct-certs">
                {[
                  { label: "B-BBEE Level", value: "1" },
                  { label: "Ownership",   value: "100% Black Owned" },
                  { label: "Established", value: "2021" },
                ].map((c, i) => (
                  <li key={i}>
                    <span>{c.label}</span>
                    <strong>{c.value}</strong>
                  </li>
                ))}
              </ul>
            </div>

            <div className="ct-aside__block ct-aside__block--cta">
              <p>Need a quote urgently?</p>
              <a href="tel:+27120041000" className="ct-aside__call-btn">
                <FiPhone size={15} /> 012 004-1000
              </a>
              <a href="tel:+27726234209" className="ct-aside__call-btn ct-aside__call-btn--secondary">
                <FiPhone size={15} /> 072 623 4209
              </a>
            </div>

          </aside>
        </div>
      </section>

      {/* ══════ 4. MAP ══════ */}
      <section className="ct-map" ref={mapRef}>
        <div className={`ct-map__label-bar fade-in ${mapInView ? "visible" : ""}`}>
          <div className="container ct-map__label-inner">
            <div>
              <div className="ct-section-tag" style={{ marginBottom: 8 }}>HEAD OFFICE</div>
              <p className="ct-map__address">
                52 Jakaranda Street, Hennopspark, Centurion, 0157
              </p>
            </div>
            <a
              href="https://maps.google.com/?q=52+Jakaranda+Street,+Hennopspark,+Centurion,+0157"
              target="_blank"
              rel="noopener noreferrer"
              className="ct-map__dir-btn"
            >
              Get Directions <FiArrowRight size={13} />
            </a>
          </div>
        </div>
        <div className={`ct-map__embed fade-in ${mapInView ? "visible" : ""}`} style={{ transitionDelay: "0.1s" }}>
          <iframe
            title="Bakwena Kit Head Office"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3594.0!2d28.1890!3d-25.8520!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e956f9b1f6c3a1f%3A0x1234567890abcdef!2s52+Jakaranda+Street%2C+Hennopspark%2C+Centurion%2C+0157!5e0!3m2!1sen!2sza!4v1700000000000"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

    </main>
  );
}